import type { Session, SupabaseClient } from "@supabase/supabase-js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createStartupRuntimeContext } from "../app/runtimeContext";
import { AuthService } from "../application/authService";
import { formatAppEnvError, parseAppEnv, type AppEnv } from "../config/env";
import { createSupabaseBrowserClient } from "../lib/supabase/client";
import {
  BrowserObservabilityClient,
  CompositeObservabilityClient,
  SupabaseObservabilityClient,
} from "../observability/clients";
import type { ObservabilityClient } from "../observability/types";
import { createSupabaseRepositories } from "../repositories/supabaseRepositories";
import { createRuntimeContainer } from "../runtime/container/defaultContainer";
import type { RuntimeContainer } from "../runtime/container/container";
import { StartupError } from "../routes/StartupError";
import {
  runSystemHealthChecks,
  type HealthCheckResult,
} from "../services/health";
import type { Database } from "../types/database";
import { resolveAccess } from "./access";
import {
  AuthContext,
  type AuthContextValue,
  type AuthStatus,
} from "./AuthContext";

type AuthRuntime = {
  appEnv: AppEnv;
  authService: AuthService;
  observability: ObservabilityClient;
  runtimeContainer: RuntimeContainer;
  supabase: SupabaseClient<Database>;
};

function createAuthRuntime(appEnv: AppEnv): AuthRuntime {
  const runtimeContext = createStartupRuntimeContext(appEnv.VITE_APP_ENV);
  const supabase = createSupabaseBrowserClient(appEnv);
  const repositories = createSupabaseRepositories({ supabase });
  const observability = new CompositeObservabilityClient([
    new BrowserObservabilityClient(runtimeContext),
    new SupabaseObservabilityClient(runtimeContext, supabase),
  ]);

  return {
    appEnv,
    supabase,
    observability,
    runtimeContainer: createRuntimeContainer({
      observability,
      environment: appEnv.VITE_APP_ENV,
      supabase,
    }),
    authService: new AuthService(
      supabase,
      repositories.profileRepository,
      repositories.authorizationRepository,
      repositories.auditRepository,
    ),
  };
}

const OAUTH_CALLBACK_PARAMS = [
  "code",
  "state",
  "error",
  "error_description",
] as const;
const INITIALIZATION_TIMEOUT_MS = 10_000;

function hasOAuthCallbackParams() {
  return OAUTH_CALLBACK_PARAMS.some((param) =>
    new URLSearchParams(window.location.search).has(param),
  );
}

function clearOAuthCallbackParams() {
  const url = new URL(window.location.href);
  let changed = false;
  for (const param of OAUTH_CALLBACK_PARAMS) {
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
}

function AuthSessionProvider({
  children,
  runtime,
}: {
  children: ReactNode;
  runtime: AuthRuntime;
}) {
  const [status, setStatus] = useState<AuthStatus>("unknown");
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<AuthContextValue["profile"]>(null);
  const [roles, setRoles] = useState<AuthContextValue["roles"]>([]);
  const [error, setError] = useState<string | null>(null);
  const [healthChecks, setHealthChecks] = useState<HealthCheckResult[]>([]);
  const [isHealthRefreshing, setIsHealthRefreshing] = useState(false);
  const identityRequestRef = useRef<{
    key: string;
    generation: number;
    promise: Promise<void>;
  } | null>(null);
  const requestGenerationRef = useRef(0);
  const callbackProcessedRef = useRef(false);
  const authStateRef = useRef<{ status: AuthStatus; userId: string | null }>({
    status: "unknown",
    userId: null,
  });
  useEffect(() => {
    authStateRef.current = {
      status,
      userId: session?.user.id ?? null,
    };
  }, [session?.user.id, status]);

  const clearTransientAuthState = useCallback(
    (nextSession: Session | null = null) => {
      requestGenerationRef.current += 1;
      identityRequestRef.current = null;
      callbackProcessedRef.current = false;
      setError(null);
      setProfile(null);
      setRoles([]);
      setHealthChecks([]);
      setSession(nextSession);
    },
    [],
  );

  const resetAuthAttempt = useCallback(() => {
    clearOAuthCallbackParams();
    clearTransientAuthState(null);
    setStatus("signed_out");
  }, [clearTransientAuthState]);

  const refreshHealth = useCallback(async () => {
    setIsHealthRefreshing(true);

    try {
      const checks = await runSystemHealthChecks({
        appEnv: runtime.appEnv,
        supabase: runtime.supabase,
        observability: runtime.observability,
        ownerEmail: profile?.email ?? session?.user.email,
      });
      setHealthChecks(checks);
    } catch (healthError) {
      await runtime.observability.error({
        category: "health",
        eventName: "health_check_failed",
        message: "System health checks failed.",
        error: healthError,
      });
    } finally {
      setIsHealthRefreshing(false);
    }
  }, [profile, runtime, session]);

  const refreshIdentity = useCallback(
    async (nextSession: Session | null, source: string) => {
      const requestKey = nextSession?.user.id ?? "signed-out";
      if (identityRequestRef.current?.key === requestKey) {
        return identityRequestRef.current.promise;
      }

      const generation = requestGenerationRef.current + 1;
      requestGenerationRef.current = generation;

      const request = (async () => {
        setError(null);
        setSession(nextSession);

        if (!nextSession) {
          if (requestGenerationRef.current === generation) {
            setProfile(null);
            setRoles([]);
            setStatus("signed_out");
            clearOAuthCallbackParams();
          }
          return;
        }

        setStatus("checking");

        try {
          const identity =
            await runtime.authService.restoreAuthenticatedIdentity(nextSession);
          if (requestGenerationRef.current !== generation) return;

          setProfile(identity.profile);
          setRoles(
            identity.roles.map((role) => ({
              code: role.code,
              name: role.name,
            })),
          );

          if (identity.access.status === "authorized") {
            setStatus("authorized");
            clearOAuthCallbackParams();
            await runtime.authService.recordAuthEvent("login", "success", {
              source,
            });
          } else if (identity.access.status === "denied") {
            setStatus("denied");
            clearOAuthCallbackParams();
            await runtime.authService.recordAuthEvent("login", "denied", {
              reason: identity.access.reason,
              source,
            });
          } else {
            setStatus("signed_out");
          }
        } catch (identityError) {
          if (requestGenerationRef.current !== generation) return;
          setProfile(null);
          setRoles([]);
          setError("Unable to sign in");
          setStatus("error");
          clearOAuthCallbackParams();
          await runtime.observability.error({
            category: "auth",
            eventName: "identity_restore_failed",
            message: "Failed to restore authenticated identity.",
            error: identityError,
          });
        } finally {
          if (identityRequestRef.current?.generation === generation) {
            identityRequestRef.current = null;
          }
        }
      })();

      identityRequestRef.current = {
        key: requestKey,
        generation,
        promise: request,
      };
      return request;
    },
    [runtime],
  );

  useEffect(() => {
    let isMounted = true;
    const timeoutId = window.setTimeout(() => {
      if (!isMounted) return;
      requestGenerationRef.current += 1;
      identityRequestRef.current = null;
      setError("Unable to sign in");
      setStatus("error");
    }, INITIALIZATION_TIMEOUT_MS);

    const completeStartup = () => window.clearTimeout(timeoutId);

    runtime.supabase.auth.getSession().then(({ data, error: sessionError }) => {
      if (!isMounted) return;
      completeStartup();

      if (sessionError) {
        setError("Unable to sign in");
        setStatus("error");
        void runtime.observability.error({
          category: "auth",
          eventName: "session_restore_failed",
          message: "Supabase session restoration failed.",
          error: sessionError,
        });
        return;
      }

      if (hasOAuthCallbackParams() && !callbackProcessedRef.current) {
        callbackProcessedRef.current = true;
      }

      void refreshIdentity(data.session, "restore");
    });

    const { data: subscription } = runtime.supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "TOKEN_REFRESHED") {
          setSession(nextSession);
          return;
        }

        if (event === "SIGNED_IN") {
          const currentAuth = authStateRef.current;

          if (
            currentAuth.status === "authorized" &&
            nextSession?.user.id === currentAuth.userId
          ) {
            setSession(nextSession);
            return;
          }

          void refreshIdentity(nextSession, event.toLowerCase());
          return;
        }

        if (event === "SIGNED_OUT") {
          clearTransientAuthState(null);
          setStatus("signed_out");
          clearOAuthCallbackParams();
        }
      },
    );

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
      subscription.subscription.unsubscribe();
    };
  }, [clearTransientAuthState, refreshIdentity, runtime]);

  const access = useMemo(() => {
    if (
      status === "error" ||
      status === "signed_out" ||
      status === "unknown" ||
      status === "checking" ||
      status === "oauth_redirecting"
    ) {
      return resolveAccess(null, null, []);
    }
    return resolveAccess(session, profile, roles);
  }, [profile, roles, session, status]);

  const refreshRuntime = useCallback(async () => {
    setIsHealthRefreshing(true);
    try {
      const { data, error: sessionError } =
        await runtime.supabase.auth.getSession();
      if (sessionError) throw sessionError;

      if (data.session) {
        setSession(data.session);
      }

      const checks = await runSystemHealthChecks({
        appEnv: runtime.appEnv,
        supabase: runtime.supabase,
        observability: runtime.observability,
        ownerEmail: profile?.email ?? data.session?.user.email,
      });
      setHealthChecks(checks);
    } catch (refreshError) {
      setError("Unable to refresh runtime");
      await runtime.observability.error({
        category: "runtime",
        eventName: "runtime_refresh_failed",
        message: "Runtime refresh failed.",
        error: refreshError,
      });
    } finally {
      setIsHealthRefreshing(false);
    }
  }, [profile, runtime]);

  const signInWithGoogle = useCallback(async () => {
    clearOAuthCallbackParams();
    const shouldSignOutFirst = status === "denied";
    clearTransientAuthState(shouldSignOutFirst ? null : session);
    setStatus("oauth_redirecting");

    if (shouldSignOutFirst) {
      await runtime.supabase.auth.signOut();
    }

    const result = await runtime.authService.signInWithGoogle();
    if (result.error) {
      setError("Unable to sign in");
      setStatus("error");
      await runtime.observability.error({
        category: "auth",
        eventName: "oauth_redirect_failed",
        message: "Google OAuth redirect could not be started.",
        error: result.error,
      });
    }
    return result;
  }, [clearTransientAuthState, runtime, session, status]);

  const signOut = useCallback(async () => {
    clearTransientAuthState(null);
    clearOAuthCallbackParams();
    const { error: signOutError } = await runtime.authService.signOutOwner();

    if (signOutError) {
      setError("Unable to sign in");
      setStatus("error");
      await runtime.observability.error({
        category: "auth",
        eventName: "logout_failed",
        message: "Owner logout failed.",
        error: signOutError,
      });
      return;
    }

    setStatus("signed_out");
  }, [clearTransientAuthState, runtime]);

  useEffect(() => {
    if (
      status === "authorized" &&
      healthChecks.length === 0 &&
      !isHealthRefreshing
    ) {
      window.setTimeout(() => void refreshHealth(), 0);
    }
  }, [healthChecks.length, isHealthRefreshing, refreshHealth, status]);

  const value = useMemo<AuthContextValue>(
    () => ({
      access,
      error,
      profile,
      roles,
      session,
      signInWithGoogle,
      signOut,
      resetAuthAttempt,
      status,
      appEnv: runtime.appEnv,
      runtimeContainer: runtime.runtimeContainer,
      healthChecks,
      isHealthRefreshing,
      refreshHealth,
      refreshRuntime,
    }),
    [
      access,
      error,
      healthChecks,
      isHealthRefreshing,
      profile,
      refreshHealth,
      resetAuthAttempt,
      roles,
      runtime.appEnv,
      runtime.runtimeContainer,
      session,
      signInWithGoogle,
      signOut,
      status,
      refreshRuntime,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [runtimeResult] = useState(() => {
    const envResult = parseAppEnv(import.meta.env);

    if (!envResult.success) {
      return { ok: false as const, error: formatAppEnvError(envResult.error) };
    }

    return { ok: true as const, runtime: createAuthRuntime(envResult.data) };
  });

  if (!runtimeResult.ok) {
    return <StartupError details={runtimeResult.error} />;
  }

  return (
    <AuthSessionProvider runtime={runtimeResult.runtime}>
      {children}
    </AuthSessionProvider>
  );
}
